import { motion } from "framer-motion";
import { ArrowRight, Truck, Building2 } from "lucide-react";
import { useDemoModal } from "../context/DemoModalContext";

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#0A234A";

const NAV = [
  {
    h: "Solutions",
    items: ["For Drivers", "For Dispatchers", "For Owner-Operators", "For Depots", "For Government"],
  },
  {
    h: "Company",
    items: ["About FleetPods", "The Network", "Hub Typologies", "Careers"],
  },
  {
    h: "Resources",
    items: ["How it works", "Partnership deck", "Contact", "Press"],
  },
];

export const SiteFooter = () => {
  const { openDemo } = useDemoModal();
  return (
    <footer data-testid="site-footer" id="contact" className="relative overflow-hidden bg-white">
      {/* split CTA band */}
      <div className="mx-auto w-full max-w-[1180px] px-6 pt-20 lg:pt-28">
        <div className="mb-10 max-w-2xl">
          <p className="mb-3 text-sm font-700 uppercase tracking-[0.18em] text-[#FF5722]">Ready when you are</p>
          <h2 className="font-display text-3xl font-700 leading-[1.1] tracking-tight sm:text-5xl" style={{ color: NAVY }}>
            Two ways to get moving
            <span className="block text-[#FF5722]">with FleetPods.</span>
          </h2>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          {[
            {
              icon: Truck,
              tag: "Drivers & Fleets",
              t: "See the network in action",
              d: "Book a live walk-through of the app and hubs tailored to your routes and volumes.",
              cta: "Request a Demo",
              testid: "footer-cta-fleet",
              dark: false,
            },
            {
              icon: Building2,
              tag: "Government & Investors",
              t: "Explore the partnership",
              d: "Get the corridor plan, program economics and the market-led proposal detail.",
              cta: "Talk to our team",
              testid: "footer-cta-partner",
              tint: true,
            },
          ].map((c, i) => (
            <motion.a
              key={c.tag}
              href="#"
              data-testid={c.testid}
              onClick={(e) => { e.preventDefault(); openDemo(); }}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.1, ease: EASE }}
              className={`group relative overflow-hidden rounded-[28px] border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(10,35,74,0.35)] ${
                c.tint ? "border-[#FF5722]/20 bg-[#FFF3EF]" : "border-black/5 bg-[#F6F7FA]"
              }`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FF5722]/10">
                <c.icon className="h-6 w-6 text-[#FF5722]" />
              </span>
              <p className="mt-6 text-xs font-700 uppercase tracking-[0.16em] text-[#FF5722]">{c.tag}</p>
              <h3 className="mt-2 font-display text-2xl font-700" style={{ color: NAVY }}>
                {c.t}
              </h3>
              <p className="mt-2 max-w-md text-sm font-400 leading-relaxed text-neutral-500">{c.d}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-[15px] font-600 text-[#FF5722]">
                {c.cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </motion.a>
          ))}
        </div>
      </div>

      {/* footer nav — Footer7 layout */}
      <div className="mx-auto mt-20 w-full max-w-[1180px] px-6">
        <div className="flex w-full flex-col justify-between gap-12 border-t border-black/5 py-12 lg:flex-row lg:items-start">
          {/* left: logo + description + social */}
          <div className="flex w-full flex-col gap-6 lg:max-w-sm">
            <img src="/fleetpods_logo.png" alt="FleetPods" className="h-7 w-auto self-start" draggable="false" />
            <p className="text-sm font-400 leading-relaxed text-neutral-500">
              A managed network of secure truck hubs and rest pods, connected by one app,
              across the corridors that move the economy.
            </p>
            <p className="text-xs font-600 uppercase tracking-[0.16em] text-[#FF5722]">Smart Parking. Better Rest.</p>
            <div data-testid="footer-social-placeholder"
              className="inline-flex items-center gap-2 rounded-lg border border-dashed border-black/15 bg-[#F6F7FA] px-3 py-2 text-xs font-500 text-neutral-400">
              Social bar - to add tomorrow
            </div>
          </div>

          {/* right: link columns */}
          <div className="grid w-full gap-8 sm:grid-cols-3 lg:max-w-2xl lg:gap-16">
            {NAV.map((col) => (
              <div key={col.h}>
                <h4 className="mb-4 text-[13px] font-700 uppercase tracking-[0.12em]" style={{ color: NAVY }}>{col.h}</h4>
                <ul className="space-y-3">
                  {col.items.map((it) => (
                    <li key={it}>
                      <a href="#" className="text-sm font-400 text-neutral-500 transition-colors duration-200 hover:text-[#FF5722]">
                        {it}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-black/5 py-6 sm:flex-row">
          <p className="text-xs font-400 text-neutral-400">© {new Date().getFullYear()} FleetPods. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="text-xs font-400 text-neutral-400 transition-colors hover:text-neutral-700">Privacy Policy</a>
            <a href="#" className="text-xs font-400 text-neutral-400 transition-colors hover:text-neutral-700">Terms & Conditions</a>
            <a href="#" className="text-xs font-400 text-neutral-400 transition-colors hover:text-neutral-700">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
